import { useEffect, useState } from 'react';
import { accesClientsApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { C } from '../../theme/palette';
import { PartageInvitation } from '../portail/PortailClient.jsx';

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 8,
  padding: '8px 9px',
  fontSize: 11,
  fontFamily: 'inherit',
};
const ETATS = {
  ACTIF: '✓ Compte ouvert',
  FERME: '🔒 Accès fermé',
  INVITE: '⏳ Invité',
  EXPIREE: '⌛ Lien expiré',
};

/**
 * Les comptes d'un client dans l'espace client du magasin : qui commande en
 * ligne pour lui, qui est invité, et l'invitation d'une nouvelle personne
 * (son patron, son technicien…). La personne invitée crée son compte, puis
 * invite elle-même ses employés.
 *
 * @param {object} props propriétés
 * @param {object} props.client client du magasin
 * @returns {import('react').ReactElement} la carte des accès
 */
export function AccesEnLigneClient({ client }) {
  const [ouvert, setOuvert] = useState(false);
  const [personnes, setPersonnes] = useState(null);
  const [f, setF] = useState({ prenom: '', nom: '', email: client?.email || '' });
  const [invitee, setInvitee] = useState(null);
  const [erreur, setErreur] = useState('');
  const enregistre = typeof client?.id === 'number';

  const recharge = () =>
    accesClientsApi
      .liste(client.id)
      .then(setPersonnes)
      .catch(() => setPersonnes([]));
  useEffect(() => {
    if (ouvert && enregistre) recharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, enregistre]);

  const invite = async () => {
    if (!f.prenom.trim()) return setErreur('Indique au moins le prénom.');
    setErreur('');
    try {
      const r = await accesClientsApi.invite(client.id, {
        prenom: f.prenom.trim(),
        nom: f.nom.trim() || null,
        email: f.email.trim() || null,
      });
      setInvitee(r);
      setF({ prenom: '', nom: '', email: '' });
      recharge();
    } catch (e) {
      setErreur(e?.message || 'L’invitation n’est pas partie.');
    }
  };

  const actifs = (personnes || []).filter((p) => p.etat === 'ACTIF').length;
  return (
    <div style={{ marginTop: 8, borderTop: '1px dashed ' + C.border, paddingTop: 8 }}>
      <div
        role="button"
        onClick={() => setOuvert((v) => !v)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
          fontSize: 10.5,
          fontWeight: 800,
          color: C.navy,
        }}
      >
        <span>
          👤 Comptes en ligne{personnes ? ' (' + actifs + ')' : ''} — le client commande avec son
          mot de passe
        </span>
        <span>{ouvert ? '▲' : '▼'}</span>
      </div>
      {ouvert && !enregistre && (
        <div style={{ fontSize: 9.5, color: C.muted, marginTop: 6 }}>
          Enregistrement du client en cours…
        </div>
      )}
      {ouvert && enregistre && (
        <div style={{ marginTop: 8 }}>
          {(personnes || []).map((p) => (
            <div
              key={(p.invitation ? 'i' : 'c') + p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 0',
                borderBottom: '1px solid ' + C.border,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800 }}>
                  {[p.prenom, p.nom].filter(Boolean).join(' ')}
                  {p.role === 'RESPONSABLE' && (
                    <span style={{ color: C.teal }}> · responsable</span>
                  )}
                </div>
                <div style={{ fontSize: 8.5, color: C.muted }}>
                  {p.email || '—'} · {ETATS[p.etat] || p.etat}
                </div>
              </div>
              {p.invitation && p.etat === 'INVITE' && (
                <Btn
                  label="Annuler"
                  sm
                  ghost
                  color={C.red}
                  onClick={() => accesClientsApi.annule(client.id, p.id).then(recharge)}
                />
              )}
              {!p.invitation && (
                <Btn
                  label={p.etat === 'ACTIF' ? 'Fermer' : 'Rouvrir'}
                  sm
                  ghost
                  color={p.etat === 'ACTIF' ? C.red : C.green}
                  onClick={() =>
                    accesClientsApi.acces(client.id, p.id, p.etat !== 'ACTIF').then(recharge)
                  }
                />
              )}
            </div>
          ))}
          {personnes && personnes.length === 0 && (
            <div style={{ fontSize: 9.5, color: C.muted, marginBottom: 6 }}>
              Personne n’a encore de compte. Invite le patron ou le responsable : il invitera
              ensuite son équipe.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
            <input
              style={champ}
              placeholder="Prénom"
              value={f.prenom}
              onChange={(e) => setF({ ...f, prenom: e.target.value })}
            />
            <input
              style={champ}
              placeholder="Nom"
              value={f.nom}
              onChange={(e) => setF({ ...f, nom: e.target.value })}
            />
          </div>
          <input
            style={{ ...champ, marginTop: 6 }}
            type="email"
            placeholder="E-mail (facultatif : sinon envoie le lien par WhatsApp)"
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
          />
          {erreur && (
            <div style={{ color: C.red, fontSize: 9.5, fontWeight: 700, marginTop: 4 }}>
              {erreur}
            </div>
          )}
          <div style={{ marginTop: 6 }}>
            <Btn label="✉️ Inviter à commander en ligne" sm color={C.green} full onClick={invite} />
          </div>
          {invitee && (
            <div style={{ marginTop: 8 }}>
              <PartageInvitation invitee={invitee} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
